import { FunctionComponent, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Button, Flex, Radio, Text } from '@chakra-ui/react';
import { ModalFormData } from '../../schemas';
import { InputField } from '@/components/InputField';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useTranslation } from 'react-i18next';

type AlternativesProps = {
  index: number;
  onSave: VoidFunction;
  onCancel: VoidFunction;
  isEditing?: boolean;
};

export const Alternatives: FunctionComponent<AlternativesProps> = ({ index, onSave,onCancel, isEditing }) => {
  const { t } = useTranslation()
  const form = useFormContext<ModalFormData>();
  const formValues = form.watch();

  const { fields, append, update, remove } = useFieldArray({
    control: form.control,
    name: `questions.${index}.answer`,
  });

  const updateCorrectAlternative = (alternativeIndex: number) => {
    const allAlternatives = formValues?.questions?.[index]?.answer;
    const currentAlternative = allAlternatives?.[alternativeIndex];
    const isAlternativeAlreadyCorrect = currentAlternative?.is_correct;

    if (isAlternativeAlreadyCorrect) return;

    allAlternatives?.forEach((alternative, currentIndex) => {
      form.trigger();

      update(currentIndex, {
        ...alternative,
        is_correct: currentIndex === alternativeIndex,
      });
    });
  };

  const alternativesError = useMemo(() => {
    const error = form.formState.errors?.questions?.[index]?.answer;

    if (error?.message) {
      return form.formState.errors?.questions?.[index]?.answer?.message;
    }

    if (error?.root?.message) {
      return form.formState.errors?.questions?.[index]?.answer?.root?.message;
    }

    return undefined;
  }, [form.formState.errors?.questions, index]);

  return (
    <Flex direction='column' gap={4}>
      {fields.map((alternative, alternativeIndex) => (
        <Flex key={alternativeIndex} gap={4} alignItems='center'>
          <Text fontSize='12px' whiteSpace='nowrap'>
            {t('pages.challenges_details.tabs.tab_2.input_alternative_label', { number: alternativeIndex + 1})}*:
          </Text>

          <InputField
            withoutFloating
            {...form.register(`questions.${index}.answer.${alternativeIndex}.content`)}
            maxLength={50}
            rootProps={{
              isInvalid: !!form.formState.errors?.questions?.[index]?.answer?.[alternativeIndex]?.content,
              errorMessage:
                form.formState.errors?.questions?.[index]?.answer?.[alternativeIndex]?.content?.message,
            }}
          />

          <Flex gap={2} alignItems='center'>
            <Radio
              name={`question-${index}-alternative`}
              isChecked={alternative.is_correct}
              onChange={() => updateCorrectAlternative(alternativeIndex)}
            />

            <ChakraFeatherIcon
              name='trash-2'
              color='red.500'
              w='18px'
              onClick={() => remove(alternativeIndex)}
              cursor='pointer'
            />
          </Flex>
        </Flex>
      ))}

      {alternativesError ? (
        <Text fontSize='14px' color='red.500'>
          {alternativesError}
        </Text>
      ) : form.formState.errors?.questions?.[index]?.image ? (
        <Text fontSize='14px' color='red.500'>
          {form.formState.errors?.questions?.[index]?.image?.message?.toString()}
        </Text>
      ) : null}

      {isEditing ? (
        <Flex justifyContent='space-between' gap={4}>
          <Button
            alignSelf='flex-start'
            variant='link'
            color='brand.500'
            textDecor='underline'
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => append({ uuid: crypto.randomUUID(), content: '', is_correct: false })}
          >
            <ChakraFeatherIcon name='plus' />
            {t('pages.challenges_details.tabs.tab_2.button_add_alternative')}
          </Button>

          <Flex gap={4}>
            <Button size='xs' h='24px' onClick={onCancel} variant="link" color="red.500">
              {t('pages.challenges_details.tabs.tab_2.button_cancel')}
            </Button>

            <Button size='xs' h='24px' onClick={onSave}>
              {t('pages.challenges_details.tabs.tab_2.button_save')}
            </Button>
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  );
};
